import { Routes } from '@angular/router';
import { AuthLayoutComponent } from './core/layouts/auth-layout/auth-layout';
import { MainLayoutComponent } from './core/layouts/main-layout/main-layout';
import { ViewerLayoutComponent } from './core/layouts/viewer-layout/viewer-layout';
import { SignInComponent } from './features/auth/sign-in/sign-in';
import { OrganizationListComponent } from './features/organizations/organization-list/organization-list.component';
import { SensorListComponent } from './features/sensors/sensor-list/sensor-list.component';
import { SiteMapComponent } from './features/sites/site-map/site-map';
import { SiteDetailComponent } from './features/sites/site-detail/site-detail';
import { authGuard } from './core/auth/auth.guard';
import { initialRouteGuard } from './core/auth/initial-route.guard';
import { viewerGuard } from './core/auth/viewer.guard';
import { mainLayoutGuard } from './core/auth/main-layout.guard';
import { roleGuard } from './core/auth/role.guard';
import { featureGuard } from './core/guards/feature.guard';
import { uuidSignInGuard } from './core/auth/uuid-sign-in.guard';
import { DigitalSignageComponent } from './features/digital-signage/digital-signage';
import { QViewerComponent } from './features/q-viewer/q-viewer';
import { SiteListComponent } from './features/sites/site-list/site-list.component';
import { SiteDetailTiltComponent } from './features/sites/site-detail-tilt/site-detail-tilt';
import { AccountListComponent } from './features/accounts/account-list/account-list.component';
import { AccountCreateComponent } from './features/accounts/account-create/account-create.component';
import { SiteManageComponent } from './features/sites/site-manage/site-manage';
import { SiteFormComponent } from './features/sites/site-form/site-form';
import { SiteSensorManageComponent } from './features/sites/site-sensor-manage/site-sensor-manage';
import { SiteDrawingManageComponent } from './features/sites/site-drawing-manage/site-drawing-manage';
import { SiteAlertManageComponent } from './features/sites/site-alert-manage/site-alert-manage';
import { DataExtractComponent } from './features/data/extract/data-extract';
import { DataDownloadComponent } from './features/data/download/data-download';
import { AccessLogComponent } from './features/data/access-log/access-log';
import { PdfViewerComponent } from './features/data/pdf-viewer/pdf-viewer';

export const routes: Routes = [
  {
    path: 'digital-signage/:uuid',
    canActivate: [uuidSignInGuard, featureGuard],
    data: { featureId: 'digital_signage' },
    title: 'デジタルサイネージ',
    component: DigitalSignageComponent,
  },
  {
    path: 'q/:uuid',
    canActivate: [uuidSignInGuard],
    title: 'アンケート',
    component: QViewerComponent,
  },
  {
    path: '',
    component: ViewerLayoutComponent,
    canMatch: [viewerGuard],
    canActivate: [authGuard],
    children: [
      { path: '', redirectTo: 'sites/detail', pathMatch: 'full' },
      { path: 'sites/detail', component: SiteDetailComponent, title: '現場詳細' },
    ],
  },
  {
    path: '',
    component: MainLayoutComponent,
    canMatch: [mainLayoutGuard],
    canActivate: [authGuard],
    children: [
      { path: '', canActivate: [initialRouteGuard], children: [] },
      {
        path: 'organizations',
        component: OrganizationListComponent,
        canActivate: [roleGuard],
        data: { minRole: 'super_admin' },
        title: '組織一覧',
      },
      {
        path: 'departments',
        loadComponent: () =>
          import('./features/departments/department-list/department-list.component').then(
            (m) => m.DepartmentListComponent,
          ),
        canActivate: [roleGuard],
        data: { minRole: 'org_admin' },
        title: '管理部署',
      },
      {
        path: 'sensors',
        component: SensorListComponent,
        canActivate: [roleGuard],
        data: { minRole: 'org_admin' },
        title: 'センサー管理',
      },
      {
        path: 'sites/list',
        component: SiteListComponent,
        canActivate: [roleGuard],
        data: { minRole: 'dep_admin' },
        title: '現場一覧',
      },
      {
        path: 'sites/map',
        component: SiteMapComponent,
        canActivate: [roleGuard],
        data: { minRole: 'dep_admin' },
        title: '現場マップ',
      },
      {
        path: 'sites/detail/:id',
        component: SiteDetailComponent,
        canActivate: [roleGuard],
        data: { minRole: 'site_manager' },
        title: '現場詳細',
      },
      {
        path: 'sites/detail-tilt/:id',
        component: SiteDetailTiltComponent,
        canActivate: [roleGuard],
        data: { minRole: 'site_manager' },
        title: '傾斜変位詳細',
      },
      {
        path: 'data/extract',
        component: DataExtractComponent,
        canActivate: [roleGuard, featureGuard],
        data: { minRole: 'site_manager', featureId: 'sensor_extract' },
        title: 'データ抽出',
      },
      {
        path: 'data/download',
        component: DataDownloadComponent,
        canActivate: [roleGuard],
        data: { minRole: 'site_manager' },
        title: 'データDL',
      },
      {
        path: 'data/pdf/:filename',
        component: PdfViewerComponent,
        canActivate: [roleGuard],
        data: { minRole: 'site_manager' },
        title: 'PDFプレビュー',
      },
      {
        path: 'data/access-log',
        component: AccessLogComponent,
        canActivate: [roleGuard],
        data: { minRole: 'super_admin' },
        title: 'アクセスログ',
      },
      {
        path: 'sites/manage',
        component: SiteManageComponent,
        canActivate: [roleGuard],
        data: { minRole: 'org_admin' },
        title: '現場管理',
      },
      {
        path: 'sites/create',
        component: SiteFormComponent,
        canActivate: [roleGuard],
        data: { minRole: 'org_admin' },
        title: '現場登録',
      },
      {
        path: 'sites/edit/:id',
        component: SiteFormComponent,
        canActivate: [roleGuard],
        data: { minRole: 'org_admin' },
        title: '現場編集',
      },
      {
        path: 'sites/sensors/:id',
        component: SiteSensorManageComponent,
        canActivate: [roleGuard],
        data: { minRole: 'org_admin' },
        title: 'センサー紐付け',
      },
      {
        path: 'sites/drawings/:siteId',
        component: SiteDrawingManageComponent,
        canActivate: [roleGuard],
        data: { minRole: 'org_admin' },
        title: '現場配置図',
      },
      {
        path: 'sites/alerts/:id',
        component: SiteAlertManageComponent,
        canActivate: [roleGuard],
        data: { minRole: 'org_admin' },
        title: '現場通知設定',
      },
      {
        path: 'settings/org',
        loadComponent: () =>
          import('./features/settings/org-settings/org-settings').then(
            (m) => m.OrgSettingsComponent,
          ),
        canActivate: [roleGuard],
        data: { minRole: 'org_admin', excludeRoles: ['super_admin'] },
        title: '組織設定',
      },
      {
        path: 'accounts/list',
        component: AccountListComponent,
        canActivate: [roleGuard],
        data: { minRole: 'org_admin' },
        title: 'ユーザー管理',
      },
      {
        path: 'accounts/create',
        component: AccountCreateComponent,
        canActivate: [roleGuard],
        data: { minRole: 'org_admin' },
        title: 'ユーザー登録',
      },
    ],
  },
  {
    path: '',
    component: AuthLayoutComponent,
    children: [{ path: 'sign-in', component: SignInComponent, title: 'ログイン' }],
  },
  {
    path: '**',
    redirectTo: '',
  },
];
